import { useState, useEffect, useCallback } from "react";
import { useStravaActivities } from "strava-acquisition/hooks/use-strava-activities";
import { useStravaOAuth } from "strava-acquisition/hooks/use-strava-oauth";
import { ActivityCard } from "strava-acquisition/components/activity-card";
import {
  FilterChips,
  DateFilterOption,
  DateRange,
} from "BEDROCK/shared-components/filter-chips";
import { MonthYearPicker } from "strava-acquisition/components/month-year-picker";
import { usePosterProjectOptional } from "PosterEditor/model/poster-project-provider";
import { extractGPSData } from "PosterEditor/utils/gps-data";
import { StravaActivityMetadata } from "PosterEditor/model/poster-project";
import { StravaActivitySummary } from "strava-acquisition/types";
import { ThreeDot } from "react-loading-indicators";
import styles from "./strava-activity-list.module.css";

export interface ActivitySelectedResult {
  activity: StravaActivitySummary;
  coordinates: { lat: number; lng: number; elevation?: number; time?: number }[];
  gpsData: {
    elevationGain: number;
    distanceMeters: number;
    start: Date | undefined;
    end: Date | undefined;
  };
  athleteFirstName: string;
}

interface StravaActivityListProps {
  onActivityApplied: () => void;
  /**
   * Optional callback for custom activity selection handling.
   * When provided, the component will fetch activity details and call this
   * instead of applying coordinates directly to the poster.
   * This is useful for pages that need to navigate elsewhere after selection.
   */
  onActivitySelected?: (result: ActivitySelectedResult) => void;
  isOpen?: boolean;
  compact?: boolean;
  className?: string;
}

type ListState =
  | {
      status: "ready";
      activities: StravaActivitySummary[];
      athlete: { id: number; firstName: string };
    }
  | { status: "selecting"; activityId: number }
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "empty";
      message: string;
      athlete: { id: number; firstName: string };
    };

export const StravaActivityList = ({
  onActivityApplied,
  onActivitySelected,
  isOpen = true,
  compact = false,
  className,
}: StravaActivityListProps) => {
  const {
    getActivitiesData,
    getAccessToken,
    getActivitiesDataForFilter,
    saveActivitiesDataForFilter,
    createActivitiesDataFromResponse,
  } = useStravaActivities();
  const { fetchActivityDetails, fetchActivitiesForDateRange } =
    useStravaOAuth();

  // Use optional hook - returns undefined when used outside ProjectProvider (e.g., /strava/activities page)
  const posterProject = usePosterProjectOptional();
  const setCoordinates = posterProject?.setCoordinates;

  const [selectedFilter, setSelectedFilter] =
    useState<DateFilterOption>("recent");
  const [customDateRange, setCustomDateRange] = useState<
    DateRange | undefined
  >();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const getInitialState = useCallback((): ListState => {
    const activitiesData = getActivitiesData();

    if (!activitiesData) {
      return {
        status: "error",
        message: "No activities found. Please connect to Strava again.",
      };
    }

    if (activitiesData.activities.length === 0) {
      return {
        status: "error",
        message: "No activities with GPS data found in your Strava account.",
      };
    }

    return {
      status: "ready",
      activities: activitiesData.activities,
      athlete: activitiesData.athlete,
    };
  }, [getActivitiesData]);

  const [state, setState] = useState<ListState>(getInitialState);

  // Reset state when component becomes visible
  useEffect(() => {
    if (isOpen) {
      setState(getInitialState());
      setSelectedFilter("recent");
      setCustomDateRange(undefined);
    }
  }, [isOpen, getInitialState]);

  const currentActivities = state.status === "ready" ? state.activities : [];

  const handleFilterChange = async (
    filter: DateFilterOption,
    range: DateRange,
  ) => {
    setSelectedFilter(filter);

    if (filter === "custom") {
      setCustomDateRange(range);
    }

    // Check if we have cached data for this filter
    const cachedData = getActivitiesDataForFilter(filter);
    if (cachedData) {
      setState({
        status: "ready",
        activities: cachedData.activities,
        athlete: cachedData.athlete,
      });
      return;
    }

    // Fetch new data for this filter
    const accessToken = getAccessToken();
    if (!accessToken) {
      setState({
        status: "error",
        message: "Session expired. Please connect to Strava again.",
      });
      return;
    }

    setState({ status: "loading" });

    // Get original athlete info to preserve it
    const originalData = getActivitiesData();
    const athleteInfo = originalData?.athlete ?? { id: 0, firstName: "" };

    try {
      const response = await fetchActivitiesForDateRange(
        accessToken,
        range.after,
        range.before,
      );

      if (!response.success) {
        const errorResponse = response as any;
        const isNoActivities = errorResponse.error?.code === "NO_ACTIVITIES";

        if (isNoActivities) {
          setState({
            status: "empty",
            message:
              "No activities with GPS data found in the selected date range.",
            athlete: athleteInfo,
          });
        } else {
          setState({
            status: "error",
            message:
              errorResponse.error?.message ||
              "Failed to fetch activities. Please try again.",
          });
        }
        return;
      }

      const responseWithToken = {
        ...response,
        accessToken,
        athlete: athleteInfo,
      };
      const activitiesData =
        createActivitiesDataFromResponse(responseWithToken);

      saveActivitiesDataForFilter(activitiesData, filter);

      if (activitiesData.activities.length === 0) {
        setState({
          status: "empty",
          message:
            "No activities with GPS data found in the selected date range.",
          athlete: athleteInfo,
        });
        return;
      }

      setState({
        status: "ready",
        activities: activitiesData.activities,
        athlete: athleteInfo,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setState({
        status: "error",
        message: `Failed to load activities: ${errorMessage}`,
      });
    }
  };

  const handlePickMonthClick = () => {
    setIsPickerOpen(true);
  };

  const handleMonthYearSelect = (range: DateRange) => {
    handleFilterChange("custom", range);
  };

  const handleActivitySelect = async (activityId: number) => {
    if (state.status !== "ready") return;

    const selectedActivity = state.activities.find((a) => a.id === activityId);
    if (!selectedActivity) return;

    const accessToken = getAccessToken();
    if (!accessToken) {
      setState({
        status: "error",
        message: "Session expired. Please connect to Strava again.",
      });
      return;
    }

    setState({
      status: "selecting",
      activityId,
    });

    try {
      const response = await fetchActivityDetails(activityId, accessToken);

      if (!response.coordinates || response.coordinates.length === 0) {
        setState({
          status: "error",
          message:
            "This activity doesn't have GPS data. Please select another activity.",
        });
        return;
      }

      const gpsData = extractGPSData({
        allCoordinates: response.coordinates,
      }) ?? {
        elevationGain: response.activity!.totalElevationGain,
        distanceMeters: response.activity!.distance,
        start: new Date(response.activity!.startDate),
        end: undefined,
      };

      // If custom handler provided, use it instead of default behavior
      if (onActivitySelected) {
        onActivitySelected({
          activity: selectedActivity,
          coordinates: response.coordinates,
          gpsData,
          athleteFirstName: state.status === "ready" ? state.athlete.firstName : "",
        });
        onActivityApplied();
        return;
      }

      if (setCoordinates) {
        // Build Strava metadata from the selected activity and response
        const athleteFirstName = state.status === "ready" ? state.athlete.firstName : "";
        const stravaMetadata: StravaActivityMetadata = {
          activityId: selectedActivity.id,
          activityName: selectedActivity.name,
          activityType: selectedActivity.type,
          startDate: selectedActivity.start_date_local,
          distance: selectedActivity.distance,
          elevationGain: selectedActivity.total_elevation_gain,
          movingTime: selectedActivity.moving_time,
          athleteFirstName,
          importedAt: Date.now(),
        };
        setCoordinates(response.coordinates, response.activity!.name, gpsData, stravaMetadata);
      }
      onActivityApplied();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setState({
        status: "error",
        message: `Failed to load activity details: ${errorMessage}`,
      });
    }
  };

  const containerClass = className
    ? `${styles.container} ${className}`
    : styles.container;

  const listClass = compact ? styles.activityListCompact : styles.activityList;

  return (
    <div className={containerClass}>
      {state.status === "ready" && (
        <>
          <header className={styles.header}>
            <h3 className={compact ? styles.titleCompact : styles.title}>
              Choose an activity
            </h3>
            <p className={compact ? styles.subtitleCompact : styles.subtitle}>
              Select the activity you want to apply to your poster
            </p>
          </header>

          <div className={styles.filterSection}>
            <FilterChips
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
              onPickMonthClick={handlePickMonthClick}
              compact={compact}
            />
          </div>

          <div className={listClass}>
            {currentActivities.map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                onSelect={handleActivitySelect}
                isSelected={false}
              />
            ))}
          </div>
        </>
      )}

      {(state.status === "loading" || state.status === "selecting") && (
        <div className={compact ? styles.loadingStateCompact : styles.loadingState}>
          <ThreeDot color="var(--color-strava-primary)" size="medium" text="" textColor="" />
          <p className={compact ? styles.loadingTextCompact : styles.loadingText}>
            {state.status === "selecting"
              ? "Loading activity data..."
              : "Loading activities..."}
          </p>
        </div>
      )}

      {state.status === "error" && (
        <div className={compact ? styles.errorStateCompact : styles.errorState}>
          <div className={compact ? styles.errorIconCompact : styles.errorIcon}>!</div>
          <p className={compact ? styles.errorMessageCompact : styles.errorMessage}>
            {state.message}
          </p>
        </div>
      )}

      {state.status === "empty" && (
        <>
          <header className={styles.header}>
            <h3 className={compact ? styles.titleCompact : styles.title}>
              Choose an activity
            </h3>
            <p className={compact ? styles.subtitleCompact : styles.subtitle}>
              Select the activity you want to apply to your poster
            </p>
          </header>

          <div className={styles.filterSection}>
            <FilterChips
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
              onPickMonthClick={handlePickMonthClick}
              compact={compact}
            />
          </div>

          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>{state.message}</p>
            <p className={styles.emptyHint}>
              Try selecting a different date range above.
            </p>
          </div>
        </>
      )}

      <MonthYearPicker
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={handleMonthYearSelect}
      />
    </div>
  );
};
